import { useState } from 'react';
import type { RoleDefinition, ScoringWeights } from '../../types/api';
import { DEFAULT_WEIGHTS, ROLE_PRESETS } from '../../config/presets';
import HelpBubble from '../ui/HelpBubble';

interface RoleFormProps {
  role: RoleDefinition;
  onChange: (role: RoleDefinition) => void;
  onLaunch: () => void;
}

const COMPANY_TYPES = [
  'Fortune 500',
  'Growth-stage',
  'Technology Vendor',
  'Consulting',
];

const JOB_CLASSIFICATIONS = [
  { value: 'office', label: 'On-site / Office' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'remote', label: 'Remote' },
];

const WEIGHT_LABELS: { key: keyof ScoringWeights; label: string }[] = [
  { key: 'qualifications', label: 'Qualifications' },
  { key: 'outcomes', label: 'Desired Outcomes' },
  { key: 'experience', label: 'Years of Experience' },
  { key: 'certifications', label: 'Certifications' },
  { key: 'education', label: 'Education' },
  { key: 'companyFit', label: 'Company / Culture Fit' },
];

const WEIGHT_LEVEL_LABELS = ['', 'Low', 'Below Avg', 'Medium', 'Above Avg', 'Critical'];

export default function RoleForm({ role, onChange, onLaunch }: RoleFormProps) {
  const [showWeights, setShowWeights] = useState(false);
  const [activePresetId, setActivePresetId] = useState(ROLE_PRESETS[0].id);

  const weights = role.weights ?? { ...DEFAULT_WEIGHTS };

  const updateField = <K extends keyof RoleDefinition>(
    field: K,
    value: RoleDefinition[K],
  ) => {
    onChange({ ...role, [field]: value });
  };

  const updateWeight = (key: keyof ScoringWeights, value: number) => {
    onChange({ ...role, weights: { ...weights, [key]: value } });
  };

  const selectPreset = (presetId: string) => {
    const preset = ROLE_PRESETS.find((p) => p.id === presetId);
    if (preset) {
      setActivePresetId(presetId);
      onChange({ ...preset.role });
    }
  };

  const updateListItem = (
    field: 'qualifications' | 'outcomes',
    index: number,
    value: string,
  ) => {
    const updated = [...role[field]];
    updated[index] = value;
    updateField(field, updated);
  };

  const addListItem = (field: 'qualifications' | 'outcomes') => {
    updateField(field, [...role[field], '']);
  };

  const removeListItem = (field: 'qualifications' | 'outcomes', index: number) => {
    if (role[field].length <= 1) return;
    const updated = role[field].filter((_, i) => i !== index);
    updateField(field, updated);
  };

  const activePreset = ROLE_PRESETS.find((p) => p.id === activePresetId);

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {/* Section header */}
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-2">
          <h2 className="text-2xl font-bold text-ti-text">
            Define Your Ideal Candidate
          </h2>
          <HelpBubble
            size="md"
            content="62% of companies unintentionally filter out qualified candidates due to overly rigid job descriptions. Try different role presets below to see how the same three candidates score very differently depending on what the role actually requires."
            source="Harvard Business Review / Psico-Smart (2024)"
          />
        </div>
        <p className="text-sm text-ti-text-muted">
          Select a pre-built role or customize your own, then launch discovery agents
        </p>
      </div>

      {/* Preset selector */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-ti-text-muted uppercase tracking-wider">
            Role Presets
          </span>
          <HelpBubble
            content="Try switching between presets and re-running discovery to see how the same three candidates score very differently. Each role shifts the qualifications, outcomes, and scoring weights — revealing how much your hiring criteria shape who gets through the door. Edit any field after selecting a preset to test your own scenarios."
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {ROLE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              onClick={() => selectPreset(preset.id)}
              className={`
                px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 cursor-pointer border
                ${activePresetId === preset.id
                  ? 'bg-ti-blue/15 border-ti-blue/50 text-ti-blue shadow-[0_0_8px_rgba(59,130,246,0.15)]'
                  : 'bg-ti-surface border-ti-border text-ti-text-muted hover:border-ti-text-dim hover:text-ti-text'
                }
              `}
            >
              {preset.shortLabel}
            </button>
          ))}
        </div>
        {/* Preset description */}
        {activePreset && (
          <div className="bg-ti-bg/50 border border-ti-border/50 rounded-lg px-4 py-3">
            <p className="text-xs text-ti-text-dim leading-relaxed">{activePreset.description}</p>
          </div>
        )}
      </div>

      {/* Form card */}
      <div className="bg-ti-surface border border-ti-border rounded-xl p-6 md:p-8 space-y-6">
        {/* Title */}
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-ti-text-muted uppercase tracking-wider">
            Role Title
          </label>
          <input
            type="text"
            value={role.title}
            onChange={(e) => updateField('title', e.target.value)}
            className="w-full bg-ti-bg border border-ti-border rounded-lg px-4 py-3 text-lg font-medium text-ti-text placeholder:text-ti-text-dim focus:outline-none focus:border-ti-blue transition-colors"
            placeholder="e.g. VP, Workforce Transformation"
          />
        </div>

        {/* Qualifications */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-ti-text-muted uppercase tracking-wider">
                Key Qualifications
              </label>
              <HelpBubble
                content="These become the keywords an ATS scans for. Notice how the ATS treats every keyword equally, while the Intelligence Engine weighs them against actual candidate evidence. Try adding unusual qualifications to see how each system reacts."
              />
            </div>
            <button
              onClick={() => addListItem('qualifications')}
              className="flex items-center gap-1 text-xs text-ti-blue hover:text-ti-cyan transition-colors cursor-pointer"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              Add
            </button>
          </div>
          <div className="space-y-2">
            {role.qualifications.map((q, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text"
                  value={q}
                  onChange={(e) => updateListItem('qualifications', i, e.target.value)}
                  className="flex-1 bg-ti-bg border border-ti-border rounded-lg px-4 py-2 text-sm text-ti-text placeholder:text-ti-text-dim focus:outline-none focus:border-ti-blue transition-colors"
                  placeholder="Enter qualification..."
                />
                <button
                  onClick={() => removeListItem('qualifications', i)}
                  disabled={role.qualifications.length <= 1}
                  className="p-2 text-ti-text-dim hover:text-ti-red disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Outcomes */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-ti-text-muted uppercase tracking-wider">
                Desired Outcomes
              </label>
              <HelpBubble
                content="Outcomes describe what this person needs to achieve, not just what they need to know. An ATS can't evaluate whether a candidate can deliver outcomes — it can only check if outcome keywords appear in their resume. The Intelligence Engine analyzes evidence of past outcomes to predict future delivery."
              />
            </div>
            <button
              onClick={() => addListItem('outcomes')}
              className="flex items-center gap-1 text-xs text-ti-blue hover:text-ti-cyan transition-colors cursor-pointer"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              Add
            </button>
          </div>
          <div className="space-y-2">
            {role.outcomes.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text"
                  value={o}
                  onChange={(e) => updateListItem('outcomes', i, e.target.value)}
                  className="flex-1 bg-ti-bg border border-ti-border rounded-lg px-4 py-2 text-sm text-ti-text placeholder:text-ti-text-dim focus:outline-none focus:border-ti-blue transition-colors"
                  placeholder="Enter outcome..."
                />
                <button
                  onClick={() => removeListItem('outcomes', i)}
                  disabled={role.outcomes.length <= 1}
                  className="p-2 text-ti-text-dim hover:text-ti-red disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Company Type */}
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-ti-text-muted uppercase tracking-wider">
            Company Type
          </label>
          <select
            value={role.companyType}
            onChange={(e) => updateField('companyType', e.target.value)}
            className="w-full bg-ti-bg border border-ti-border rounded-lg px-4 py-2.5 text-sm text-ti-text focus:outline-none focus:border-ti-blue transition-colors cursor-pointer appearance-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23737373' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 12px center',
            }}
          >
            {COMPANY_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
        {/* Additional criteria row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-ti-border/50">
          {/* Years of Experience */}
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-ti-text-muted uppercase tracking-wider">
              Years of Experience
            </label>
            <input
              type="text"
              value={role.yearsExperience}
              onChange={(e) => updateField('yearsExperience', e.target.value)}
              className="w-full bg-ti-bg border border-ti-border rounded-lg px-4 py-2.5 text-sm text-ti-text placeholder:text-ti-text-dim focus:outline-none focus:border-ti-blue transition-colors"
              placeholder="e.g. 15+"
            />
          </div>

          {/* Certifications */}
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-ti-text-muted uppercase tracking-wider">
              Certifications
            </label>
            <input
              type="text"
              value={role.certifications}
              onChange={(e) => updateField('certifications', e.target.value)}
              className="w-full bg-ti-bg border border-ti-border rounded-lg px-4 py-2.5 text-sm text-ti-text placeholder:text-ti-text-dim focus:outline-none focus:border-ti-blue transition-colors"
              placeholder="e.g. Six Sigma, PMP, COPC"
            />
          </div>

          {/* Job Classification */}
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-ti-text-muted uppercase tracking-wider">
              Job Classification
            </label>
            <select
              value={role.jobClassification}
              onChange={(e) => updateField('jobClassification', e.target.value as 'office' | 'hybrid' | 'remote')}
              className="w-full bg-ti-bg border border-ti-border rounded-lg px-4 py-2.5 text-sm text-ti-text focus:outline-none focus:border-ti-blue transition-colors cursor-pointer appearance-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23737373' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
              }}
            >
              {JOB_CLASSIFICATIONS.map((jc) => (
                <option key={jc.value} value={jc.value}>{jc.label}</option>
              ))}
            </select>
          </div>

          {/* Education */}
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-ti-text-muted uppercase tracking-wider">
              Education
            </label>
            <input
              type="text"
              value={role.education}
              onChange={(e) => updateField('education', e.target.value)}
              className="w-full bg-ti-bg border border-ti-border rounded-lg px-4 py-2.5 text-sm text-ti-text placeholder:text-ti-text-dim focus:outline-none focus:border-ti-blue transition-colors"
              placeholder="e.g. Bachelor's required, MBA preferred"
            />
          </div>
        </div>
      </div>

      {/* Scoring Weights Panel */}
      <div className="bg-ti-surface border border-ti-border rounded-xl overflow-hidden">
        <button
          onClick={() => setShowWeights(!showWeights)}
          className="w-full flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-ti-bg/30 transition-colors"
        >
          <div className="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-ti-text-muted">
              <path d="M2 4H14M4 4V2M4 4V6M2 8H14M10 8V6M10 8V10M2 12H14M7 12V10M7 12V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <span className="text-xs font-semibold text-ti-text-muted uppercase tracking-wider">
              Scoring Weights
            </span>
            <HelpBubble
              content="Daniel Kahneman's research showed that &quot;unwanted variability in decisions that should be consistent is as costly as bias but far less recognized.&quot; These weights make the Intelligence Engine's priorities transparent and controllable — unlike a traditional ATS where the algorithm is a black box."
              source="Kahneman, &quot;Noise: A Flaw in Human Judgment&quot;"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-ti-text-dim">
              {showWeights ? 'Hide' : 'Customize how the agent scores candidates'}
            </span>
            <svg
              width="12" height="12" viewBox="0 0 12 12" fill="none"
              className={`text-ti-text-dim transition-transform duration-200 ${showWeights ? 'rotate-180' : ''}`}
            >
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </button>

        {showWeights && (
          <div className="px-6 pb-6 space-y-4 border-t border-ti-border/50">
            <p className="text-xs text-ti-text-dim pt-4">
              Adjust the importance of each criterion. Higher weight = more influence on the Intelligence Brief score. Note: ATS scoring intentionally ignores weights — that's part of the problem.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              {WEIGHT_LABELS.map(({ key, label }) => (
                <div key={key} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs text-ti-text-muted">{label}</label>
                    <span className="text-[10px] font-mono text-ti-text-dim">
                      {WEIGHT_LEVEL_LABELS[weights[key]]} ({weights[key]}/5)
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    step="1"
                    value={weights[key]}
                    onChange={(e) => updateWeight(key, parseInt(e.target.value))}
                    className="w-full h-1.5 bg-ti-border rounded-full appearance-none cursor-pointer
                      [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                      [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-ti-blue [&::-webkit-slider-thumb]:cursor-pointer
                      [&::-webkit-slider-thumb]:shadow-[0_0_6px_rgba(59,130,246,0.4)]
                      [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full
                      [&::-moz-range-thumb]:bg-ti-blue [&::-moz-range-thumb]:border-none [&::-moz-range-thumb]:cursor-pointer"
                  />
                  <div className="flex justify-between text-[9px] text-ti-text-dim">
                    <span>Low</span>
                    <span>Critical</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Launch button */}
      <div className="flex justify-center">
        <button
          onClick={onLaunch}
          className="group flex items-center gap-3 px-8 py-4 bg-ti-blue hover:bg-ti-blue/90 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-ti-blue/20 hover:shadow-ti-blue/30 hover:scale-[1.02] cursor-pointer"
        >
          {/* Radar icon */}
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            className="group-hover:animate-spin"
            style={{ animationDuration: '3s' }}
          >
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3" />
            <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="1.5" />
            <circle cx="12" cy="12" r="2" fill="currentColor" />
            <line x1="12" y1="2" x2="12" y2="12" stroke="currentColor" strokeWidth="1.5" />
          </svg>
          Launch Discovery
        </button>
      </div>
    </div>
  );
}
